import { useState, useRef, useEffect, useCallback } from 'react'
import { Bell, CheckCheck } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notiApi, streamUrl, type FeedResponse } from '../lib/api-noti'
import { DEMO_EMPLOYEE_SUBSCRIBER, FEED_POLL_FAST, FEED_POLL_SLOW } from '../lib/config'
import { useEventSource } from '../lib/useEventSource'

// Header bell for the demo employee subscriber: unread badge + dropdown
// feed. Polling is the source of truth; the SSE stream just invalidates the
// query the moment a new notification lands (instant badge bump on demo).
export function NotificationBell() {
  const sub = DEMO_EMPLOYEE_SUBSCRIBER
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const feedKey = ['noti', 'feed', sub] as const

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: feedKey })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, sub])

  const sseConnected = useEventSource(streamUrl.subscriber(sub), invalidate)

  const { data } = useQuery({
    queryKey: feedKey,
    queryFn: () => notiApi.feed(sub, 1, 8),
    refetchInterval: sseConnected ? FEED_POLL_SLOW : FEED_POLL_FAST,
  })

  const markRead = useMutation({
    mutationFn: (id: number) => notiApi.markRead(sub, id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: feedKey })
      const prev = qc.getQueryData<FeedResponse>(feedKey)
      qc.setQueryData<FeedResponse>(feedKey, (old) =>
        old
          ? {
              ...old,
              unreadCount: Math.max(0, old.unreadCount - 1),
              data: old.data.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
            }
          : old,
      )
      return { prev }
    },
    onError: (_e, _id, ctx) => ctx?.prev && qc.setQueryData(feedKey, ctx.prev),
    onSettled: invalidate,
  })

  const markAll = useMutation({
    mutationFn: () => notiApi.markAllRead(sub),
    onSettled: invalidate,
  })

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const unread = data?.unreadCount ?? 0

  return (
    <div className="bell-wrap" ref={wrapRef}>
      <button className="bell-btn" onClick={() => setOpen((v) => !v)} aria-label="การแจ้งเตือน">
        <Bell size={18} strokeWidth={2} aria-hidden />
        {unread > 0 && <span className="bell-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="feed-pop panel">
          <div className="feed-head">
            <span className="title">การแจ้งเตือน — {sub}</span>
            <button
              className="link-btn"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending || unread === 0}
              title="อ่านทั้งหมด"
            >
              <CheckCheck size={14} aria-hidden /> อ่านทั้งหมด
            </button>
          </div>
          {!data || data.data.length === 0 ? (
            <div className="feed-empty muted">ยังไม่มีการแจ้งเตือน</div>
          ) : (
            <ul className="feed-list">
              {data.data.map((n) => (
                <li
                  key={n.id}
                  className={`feed-item ${n.readAt ? '' : 'unread'}`}
                  onClick={() => !n.readAt && markRead.mutate(n.id)}
                >
                  <div className="t">{n.title}</div>
                  <div className="b">{n.body}</div>
                  <div className="m muted">
                    {n.workflowKey} · {new Date(n.createdAt).toLocaleString('th-TH')}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="feed-foot muted">
            {sseConnected ? 'live · SSE connected' : `polling ทุก ${FEED_POLL_FAST / 1000} วินาที`}
          </div>
        </div>
      )}
    </div>
  )
}
